import { useMutation, useQuery } from '@apollo/client';
import { browserLogger } from '@newfront-insurance/dd-rum';
import { AuthOptions } from '@newfront-insurance/ms-graph-auth';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { NovaSettingsData, NovaSettingField } from '../../../hooks/use-nova-settings';
import {
  DISCONNECT_NOVA_MICROSOFT_GRAPH_CONNECTION,
  GET_NOVA_MICROSOFT_GRAPH_CONNECTION_STATUS,
  START_NOVA_MICROSOFT_GRAPH_AUTHORIZATION,
} from '../../../queries/mutations';
import { useMSGraphAuth } from '../contexts/ms-graph-context';
import {
  clearStoredMicrosoftGraphAuthorizationResult,
  clearStoredMicrosoftGraphAuthorizationState,
  NOVA_MICROSOFT_GRAPH_AUTHORIZATION_MESSAGE_TYPE,
  NOVA_MICROSOFT_GRAPH_AUTHORIZATION_POPUP_FEATURES,
  NOVA_MICROSOFT_GRAPH_AUTHORIZATION_RESULT_KEY,
  writeStoredMicrosoftGraphAuthorizationState,
} from '../lib/microsoft-graph-authorization';

export interface OutlookConnectController {
  // State
  outlookConnectEnabled: boolean;

  /** True when Outlook is enabled but auth has expired and interactive login is required. */
  outlookNeedsReauth: boolean;

  // Actions
  setOutlookConnectEnabled(enabled: boolean): void;

  /**
   * Attempt interactive login if Outlook is enabled but auth has expired.
   * Must be called from a user gesture (click handler) so the popup is not blocked.
   * Returns true if a login popup was triggered.
   */
  reconnectIfNeeded(): Promise<boolean>;
}

export function useOutlookConnectController({
  msGraphEnabled,
  delegatedGraphConnectionsEnabled,
  canConnectOutlook,
  isChatVisible,
  notify,
  serverSettings,
  onPersist,
}: {
  msGraphEnabled: boolean;
  delegatedGraphConnectionsEnabled: boolean;
  canConnectOutlook: boolean;
  isChatVisible: boolean;
  notify: { success: (msg: string) => void; error: (msg: string) => void };
  serverSettings?: NovaSettingsData | null;
  onPersist?: (field: NovaSettingField, value: unknown) => void;
}): OutlookConnectController {
  const msGraphAuth = useMSGraphAuth();
  const [startNovaMicrosoftGraphAuthorization] = useMutation(START_NOVA_MICROSOFT_GRAPH_AUTHORIZATION);
  const [disconnectNovaMicrosoftGraphConnection] = useMutation(DISCONNECT_NOVA_MICROSOFT_GRAPH_CONNECTION);
  const isOutlookConnectAvailable = (msGraphEnabled || delegatedGraphConnectionsEnabled) && canConnectOutlook;

  // Initialize from server settings if available, default to false
  const [outlookConnectEnabled, setOutlookConnectEnabledState] = useState(
    () => isOutlookConnectAvailable && (serverSettings?.outlookConnectEnabled ?? false),
  );
  const [outlookNeedsReauth, setOutlookNeedsReauth] = useState(false);
  const { data: delegatedConnectionStatusData, refetch: refetchDelegatedConnectionStatus } = useQuery(
    GET_NOVA_MICROSOFT_GRAPH_CONNECTION_STATUS,
    {
      fetchPolicy: 'cache-and-network',
      // Nova can mount on the standalone chat route or as the global overlay.
      // Only resolve saved delegated connection health when Outlook Connect is
      // enabled, so page load with the toggle off does not validate the new BFF
      // GraphQL field.
      skip: !delegatedGraphConnectionsEnabled || !canConnectOutlook || !outlookConnectEnabled,
    },
  );
  const delegatedConnectionStatus = delegatedConnectionStatusData?.getNovaMicrosoftGraphConnectionStatus;

  // Sync from server settings when they arrive
  useEffect(() => {
    if (serverSettings != null) {
      setOutlookConnectEnabledState(isOutlookConnectAvailable && serverSettings.outlookConnectEnabled);
    }
  }, [isOutlookConnectAvailable, serverSettings]);

  useEffect(() => {
    if (!canConnectOutlook) {
      setOutlookConnectEnabledState(false);
      setOutlookNeedsReauth(false);
    }
  }, [canConnectOutlook]);

  useEffect(() => {
    if (!delegatedGraphConnectionsEnabled) {
      return;
    }

    setOutlookNeedsReauth(outlookConnectEnabled && (delegatedConnectionStatus?.needsReauth ?? false));
  }, [delegatedGraphConnectionsEnabled, delegatedConnectionStatus?.needsReauth, outlookConnectEnabled]);

  // Feature flag kill switch - automatically disconnect when both graph auth modes are OFF
  useEffect(() => {
    if (!isOutlookConnectAvailable && outlookConnectEnabled) {
      setOutlookConnectEnabledState(false);
      onPersist?.('outlookConnectEnabled', false);
    }
  }, [isOutlookConnectAvailable, outlookConnectEnabled, onPersist]);

  // Proactive re-authentication: When Nova window is opened and toggle is ON but user is logged out, trigger login
  // This ensures toggle state always matches auth state when user actively opens Nova (toggle ON = authenticated)
  const authStateValue = msGraphAuth?.authState.state;

  // Track whether we've already attempted silent reconnect this session.
  // Prevents infinite loops: loginSilent fails → state becomes INTERACTION_REQUIRED →
  // effect re-fires → loginSilent again → loop.
  const silentReconnectAttemptedRef = useRef(false);
  const delegatedAuthorizationCleanupRef = useRef<(() => void) | null>(null);

  useEffect(
    () => () => {
      delegatedAuthorizationCleanupRef.current?.();
    },
    [],
  );

  // Reset the flag when the user explicitly logs in successfully (e.g. via toggle)
  // so that future disconnects can attempt silent reconnect again.
  useEffect(() => {
    if (authStateValue === AuthOptions.LOGGED_IN) {
      silentReconnectAttemptedRef.current = false;
    }
  }, [authStateValue]);

  useEffect(() => {
    if (
      isChatVisible &&
      outlookConnectEnabled &&
      !delegatedGraphConnectionsEnabled &&
      msGraphAuth &&
      !silentReconnectAttemptedRef.current &&
      (authStateValue === AuthOptions.LOGGED_OUT || authStateValue === AuthOptions.INTERACTION_REQUIRED)
    ) {
      silentReconnectAttemptedRef.current = true;

      browserLogger.info(
        '[NovaProvider] Nova opened with toggle ON but user logged out - attempting silent re-authentication',
        { authState: authStateValue },
      );
      // Silent-only — never open a popup automatically. Popups should only be
      // triggered by the user explicitly toggling Outlook on. With cacheLocation
      // set to localStorage, silent auth succeeds in steady state. If it fails
      // (e.g. first load after cache migration), the user can re-toggle.
      msGraphAuth
        .loginSilent()
        .then((authState) => {
          browserLogger.info('[NovaProvider] Silent re-authentication completed', {
            newAuthState: authState.state,
            wasSuccessful: authState.state === AuthOptions.LOGGED_IN,
          });
          if (authState.state === AuthOptions.LOGGED_IN) {
            notify.success('Outlook connected successfully');
            setOutlookNeedsReauth(false);
          } else {
            setOutlookNeedsReauth(true);
          }
        })
        .catch((error) => {
          browserLogger.error('[NovaProvider] Silent re-authentication failed', error);
          setOutlookNeedsReauth(true);
        });
    }
  }, [isChatVisible, outlookConnectEnabled, delegatedGraphConnectionsEnabled, authStateValue, msGraphAuth, notify]);

  // Disable Outlook and reset state
  const disableOutlook = useCallback(() => {
    setOutlookConnectEnabledState(false);
    setOutlookNeedsReauth(false);
    onPersist?.('outlookConnectEnabled', false);

    if (delegatedGraphConnectionsEnabled) {
      // If the user turns the toggle off while an OAuth popup is still in
      // flight, stop listening for that stale callback before revoking the
      // connection. Otherwise a late callback can update the UI after the user
      // explicitly disabled Outlook Connect.
      delegatedAuthorizationCleanupRef.current?.();
      clearStoredMicrosoftGraphAuthorizationResult();
      clearStoredMicrosoftGraphAuthorizationState();

      disconnectNovaMicrosoftGraphConnection()
        .then(({ data }) => {
          browserLogger.info('[OutlookConnect] Delegated MS Graph connection disconnected', {
            connectionUuid: data?.disconnectNovaMicrosoftGraphConnection.connectionUuid,
            status: data?.disconnectNovaMicrosoftGraphConnection.status,
          });
          void refetchDelegatedConnectionStatus();
        })
        .catch((error) => {
          browserLogger.error('[OutlookConnect] Failed to disconnect delegated MS Graph connection', { error });
        });
    }

    // Log out of MS Graph so toggling back ON requires re-authentication
    if (msGraphAuth) {
      msGraphAuth.logout().catch((error) => {
        browserLogger.error('[OutlookConnect] Failed to logout from MS Graph', { error });
      });
    }
  }, [
    delegatedGraphConnectionsEnabled,
    disconnectNovaMicrosoftGraphConnection,
    refetchDelegatedConnectionStatus,
    onPersist,
    msGraphAuth,
  ]);

  // Handle authentication cancellation
  const handleAuthCancelled = useCallback(() => {
    disableOutlook();
    notify.error('Outlook authentication was cancelled. Please enable again in Settings.');
  }, [disableOutlook, notify]);

  // Handle authentication failure
  const handleAuthError = useCallback(
    (error: Error) => {
      disableOutlook();
      notify.error(`Outlook authentication failed: ${error.message || 'Please try again.'}`);
    },
    [disableOutlook, notify],
  );

  // Enable Outlook and trigger authentication
  const enableOutlook = useCallback(async () => {
    if (!isOutlookConnectAvailable) {
      return;
    }

    setOutlookConnectEnabledState(true);
    onPersist?.('outlookConnectEnabled', true);

    if (delegatedGraphConnectionsEnabled) {
      // The callback page communicates completion through localStorage because
      // popup postMessage can be missed if the opener reloads or the popup is
      // closed immediately after returning from Microsoft. Clear any stale
      // result before starting so a previous failed attempt cannot disconnect
      // the fresh pending connection.
      clearStoredMicrosoftGraphAuthorizationResult();
      clearStoredMicrosoftGraphAuthorizationState();

      // Open a blank popup synchronously from the user's click. Waiting until
      // after the start mutation returns would make browsers treat it as a
      // non-user-initiated popup and block it.
      const popup = window.open(
        '',
        'nova-microsoft-graph-authorization',
        NOVA_MICROSOFT_GRAPH_AUTHORIZATION_POPUP_FEATURES,
      );

      if (!popup) {
        disableOutlook();
        notify.error('Outlook authentication popup was blocked. Please allow popups and try again.');
        return;
      }

      try {
        let completed = false;
        let loggedPopupClosed = false;
        const authorizationTimeoutRef: { current?: number } = {};
        let cleanup: () => void = () => {};
        const completeAuthorization = (status: 'success' | 'error') => {
          // Completion can arrive through both postMessage and localStorage
          // polling. Keep the handler idempotent so a successful callback does
          // not double-toast or race with cleanup.
          if (completed) {
            return;
          }

          completed = true;
          cleanup();

          if (status === 'success') {
            setOutlookNeedsReauth(false);
            void refetchDelegatedConnectionStatus();
            notify.success('Outlook connected successfully');
          } else {
            disableOutlook();
            notify.error('Outlook authentication failed: Please try again.');
          }
        };
        const pollInterval = window.setInterval(() => {
          const storedResult = localStorage.getItem(NOVA_MICROSOFT_GRAPH_AUTHORIZATION_RESULT_KEY);
          if (storedResult) {
            localStorage.removeItem(NOVA_MICROSOFT_GRAPH_AUTHORIZATION_RESULT_KEY);
            try {
              completeAuthorization(JSON.parse(storedResult).status === 'success' ? 'success' : 'error');
            } catch {
              completeAuthorization('error');
            }
            return;
          }

          // Some browsers report popup.closed unreliably once the popup has
          // navigated to login.microsoftonline.com. Treat the callback result
          // as the source of truth; using popup.closed as cancellation can
          // disconnect the backend connection while the user is still signing in.
          if (!completed && popup.closed && !loggedPopupClosed) {
            loggedPopupClosed = true;
            browserLogger.info('[OutlookConnect] Delegated MS Graph authorization popup appears closed');
          }
        }, 500);
        const handleAuthorizationMessage = (event: MessageEvent) => {
          if (
            event.origin !== window.location.origin ||
            event.data?.type !== NOVA_MICROSOFT_GRAPH_AUTHORIZATION_MESSAGE_TYPE
          ) {
            return;
          }

          completeAuthorization(event.data.status === 'success' ? 'success' : 'error');
        };
        const handleAuthorizationStorage = (event: StorageEvent) => {
          if (event.key !== NOVA_MICROSOFT_GRAPH_AUTHORIZATION_RESULT_KEY || !event.newValue) {
            return;
          }

          localStorage.removeItem(NOVA_MICROSOFT_GRAPH_AUTHORIZATION_RESULT_KEY);
          try {
            completeAuthorization(JSON.parse(event.newValue).status === 'success' ? 'success' : 'error');
          } catch {
            completeAuthorization('error');
          }
        };
        cleanup = () => {
          window.clearInterval(pollInterval);
          if (authorizationTimeoutRef.current != null) {
            window.clearTimeout(authorizationTimeoutRef.current);
          }
          window.removeEventListener('message', handleAuthorizationMessage);
          window.removeEventListener('storage', handleAuthorizationStorage);
          delegatedAuthorizationCleanupRef.current = null;
        };

        delegatedAuthorizationCleanupRef.current?.();
        delegatedAuthorizationCleanupRef.current = cleanup;
        window.addEventListener('message', handleAuthorizationMessage);
        window.addEventListener('storage', handleAuthorizationStorage);

        browserLogger.info('[OutlookConnect] Starting delegated MS Graph authorization');
        const { data } = await startNovaMicrosoftGraphAuthorization({
          variables: {
            command: {
              // users-service stores this exact redirect URI with the pending
              // connection and validates it during callback completion. Build
              // it from the current origin so local, stage, and prod each use
              // the host that initiated the popup.
              redirectUri: `${window.location.origin}/outlook-connections/oauth/callback`,
            },
          },
        });
        const authorization = data?.startNovaMicrosoftGraphAuthorization;

        if (!authorization?.authorizationUrl) {
          throw new Error('Unable to start Microsoft authorization');
        }

        // If the user abandons the popup and no callback ever arrives, fail the
        // optimistic toggle once the backend's pending authorization state has
        // expired. This avoids relying on popup.closed while still preventing an
        // indefinitely enabled UI with no usable connection.
        const authorizationExpiresAt = new Date(authorization.expiresAt).getTime();
        const authorizationTimeoutMs = Math.max(0, authorizationExpiresAt - Date.now()) + 5000;
        authorizationTimeoutRef.current = window.setTimeout(() => {
          completeAuthorization('error');
        }, authorizationTimeoutMs);

        writeStoredMicrosoftGraphAuthorizationState({
          connectionUuid: authorization.connectionUuid,
          expiresAt: authorization.expiresAt,
          // The backend-generated state is checked both here and by
          // users-service. Keeping it in browser storage lets the callback
          // page reject stray or replayed Microsoft redirects.
          state: authorization.state,
        });

        browserLogger.info('[OutlookConnect] Delegated MS Graph authorization started', {
          connectionUuid: authorization.connectionUuid,
          expiresAt: authorization.expiresAt,
        });
        popup.location.assign(authorization.authorizationUrl);
      } catch (error) {
        browserLogger.error('[OutlookConnect] Failed to start delegated MS Graph authorization', { error });
        delegatedAuthorizationCleanupRef.current?.();
        popup.close();
        disableOutlook();
        notify.error('Outlook authentication failed: Please try again.');
      }
      return;
    }

    if (!msGraphAuth) {
      return;
    }

    const isLoggedIn = msGraphAuth.authState.state === AuthOptions.LOGGED_IN;
    if (isLoggedIn) {
      // Already logged in - no toast needed, user just toggled ON
      return;
    }

    try {
      const authState = await msGraphAuth.login();
      if (authState.state === AuthOptions.LOGGED_IN) {
        // Show toast only after successful authentication
        notify.success('Outlook connected successfully');
      } else {
        handleAuthCancelled();
      }
    } catch (error) {
      handleAuthError(error as Error);
    }
  }, [
    isOutlookConnectAvailable,
    delegatedGraphConnectionsEnabled,
    startNovaMicrosoftGraphAuthorization,
    refetchDelegatedConnectionStatus,
    msGraphAuth,
    disableOutlook,
    handleAuthCancelled,
    handleAuthError,
    notify,
    onPersist,
  ]);

  // Handle Outlook Connect toggle changes
  const setOutlookConnectEnabled = useCallback(
    (enabled: boolean) => {
      if (enabled) {
        enableOutlook();
      } else {
        disableOutlook();
      }
    },
    [enableOutlook, disableOutlook],
  );

  // Attempt interactive login if Outlook is enabled but auth has expired.
  // Called from click handlers (user gesture) so the popup is allowed by the browser.
  const reconnectIfNeeded = useCallback(async (): Promise<boolean> => {
    if (delegatedGraphConnectionsEnabled) {
      if (!canConnectOutlook || !outlookConnectEnabled || !outlookNeedsReauth) return false;

      await enableOutlook();
      return true;
    }

    if (!canConnectOutlook || !outlookConnectEnabled || !msGraphAuth) return false;

    const needsLogin =
      msGraphAuth.authState.state === AuthOptions.LOGGED_OUT ||
      msGraphAuth.authState.state === AuthOptions.INTERACTION_REQUIRED ||
      msGraphAuth.authState.state === AuthOptions.ERROR;

    if (!needsLogin) return false;

    try {
      const authState = await msGraphAuth.login();
      if (authState.state === AuthOptions.LOGGED_IN) {
        setOutlookNeedsReauth(false);
        notify.success('Outlook connected successfully');
        return true;
      }
      return false;
    } catch {
      return false;
    }
  }, [
    delegatedGraphConnectionsEnabled,
    canConnectOutlook,
    outlookConnectEnabled,
    outlookNeedsReauth,
    enableOutlook,
    msGraphAuth,
    notify,
  ]);

  return useMemo(
    () => ({
      outlookConnectEnabled,
      outlookNeedsReauth,
      setOutlookConnectEnabled,
      reconnectIfNeeded,
    }),
    [outlookConnectEnabled, outlookNeedsReauth, setOutlookConnectEnabled, reconnectIfNeeded],
  );
}
